import { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext();

// helper: unique key per product + variant
const buildCartKey = (item) =>
  `${item.id}-${item.selectedVariant || "base"}`;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("cart");
    if (!savedCart) return [];

    try {
      const parsed = JSON.parse(savedCart);
      if (!Array.isArray(parsed)) return [];

      // ensure everything has a cartKey (for old carts)
      return parsed.map((item) => ({
        ...item,
        cartKey: item.cartKey || buildCartKey(item),
      }));
    } catch {
      return [];
    }
  });

  // Save to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1) => {
    setCart((prev) => {
      const cartKey = buildCartKey(product);

      const existingIndex = prev.findIndex(
        (item) => item.cartKey === cartKey
      );

      if (existingIndex !== -1) {
        // same product + same variant → bump quantity
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: (updated[existingIndex].quantity || 0) + quantity,
        };
        return updated;
      }

      // new line item
      return [
        ...prev,
        {
          ...product,
          quantity: quantity || 1,
          cartKey,
        },
      ];
    });
  };

  // keyOrId so old code that passes id still works
  const removeFromCart = (keyOrId) => {
    setCart((prev) =>
      prev.filter(
        (item) =>
          item.cartKey !== keyOrId && item.id !== keyOrId
      )
    );
  };

  const clearCart = () => setCart([]);

  const getTotalPrice = () =>
    cart.reduce(
      (total, item) =>
        total + (item.price || 0) * (item.quantity || 0),
      0
    );

  const decreaseQuantity = (keyOrId) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.cartKey === keyOrId || item.id === keyOrId) {
            return {
              ...item,
              quantity: (item.quantity || 0) - 1,
            };
          }
          return item;
        })
        .filter((item) => (item.quantity || 0) > 0)
    );
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        clearCart,
        getTotalPrice,
        decreaseQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
